<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>

                    <h1
                        id="primary-heading"
                        class="mt-2 text-2xl font-medium text-taupe-800"
                    >
                        Reports
                    </h1>
                </div>

                <!-- Denied state -->
                <zen-denied-state
                    v-if="!canViewAudit && !hasPlanFeature('audit_log_access')"
                    title="Audit log access restricted"
                    message="The audit log needs a higher plan — no sales call, just upgrade in Billing."
                />
                <zen-denied-state
                    v-else-if="!canViewAudit"
                    title="Audit log access restricted"
                    message="You don't have permission to view the audit log. Contact your account administrator to request access."
                />

                <!-- Audit content -->
                <template v-if="canViewAudit">
                    <!-- Page Title -->
                    <div
                        class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                    >
                        <h2 class="text-xl font-medium text-taupe-800">
                            Audit Log
                        </h2>
                    </div>
                    <project-change-notice
                        :project-key="project?.key"
                        @refresh="refresh"
                    />

                    <!-- Filters -->
                    <div class="bg-white shadow-sm rounded-lg p-4 space-y-4">
                        <div
                            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                        >
                            <!-- Action filter -->
                            <div>
                                <label
                                    for="filter-action"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Action
                                </label>
                                <select
                                    id="filter-action"
                                    v-model="filters.action"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                    data-testid="audit-filter-action"
                                >
                                    <option value="">All actions</option>
                                    <option
                                        v-for="action in actionOptions"
                                        :key="action.value"
                                        :value="action.value"
                                    >
                                        {{ action.label }}
                                    </option>
                                </select>
                            </div>

                            <!-- Subject type filter -->
                            <div>
                                <label
                                    for="filter-subject-type"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Subject Type
                                </label>
                                <select
                                    id="filter-subject-type"
                                    v-model="filters.subject_type"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                    data-testid="audit-filter-subject-type"
                                >
                                    <option value="">All types</option>
                                    <option
                                        v-for="type in subjectTypeOptions"
                                        :key="type"
                                        :value="type"
                                    >
                                        {{ formatSubjectType(type) }}
                                    </option>
                                </select>
                            </div>

                            <!-- Date from -->
                            <div>
                                <label
                                    for="filter-from"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    From
                                </label>
                                <input
                                    id="filter-from"
                                    type="date"
                                    v-model="filters.created_from"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                    data-testid="audit-filter-from"
                                />
                            </div>

                            <!-- Date to -->
                            <div>
                                <label
                                    for="filter-to"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    To
                                </label>
                                <input
                                    id="filter-to"
                                    type="date"
                                    v-model="filters.created_to"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                    data-testid="audit-filter-to"
                                />
                            </div>

                            <!-- Actor filter -->
                            <div>
                                <label
                                    for="filter-actor"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Actor
                                </label>
                                <select
                                    id="filter-actor"
                                    v-model="filters.actor_ulid"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                    data-testid="audit-filter-actor"
                                >
                                    <option value="">All actors</option>
                                    <option
                                        v-for="actor in actors"
                                        :key="actor.ulid"
                                        :value="actor.ulid"
                                    >
                                        {{ actor.display_name }}
                                    </option>
                                </select>
                            </div>

                            <!-- Flag filter -->
                            <div>
                                <label
                                    for="filter-flag"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Flag
                                </label>
                                <select
                                    id="filter-flag"
                                    v-model="filters.flag_ulid"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                    data-testid="audit-filter-flag"
                                >
                                    <option value="">All flags</option>
                                    <option
                                        v-for="flag in flags"
                                        :key="flag.ulid"
                                        :value="flag.ulid"
                                    >
                                        {{ flag.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <zen-button
                                variant="secondary"
                                size="field"
                                @click="clearFilters"
                                data-testid="audit-clear-filters"
                            >
                                Clear Filters
                            </zen-button>
                            <div class="flex items-center space-x-3">
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    :processing="exporting"
                                    @click="exportCsv"
                                    data-testid="audit-export-csv"
                                >
                                    Export CSV
                                </zen-button>
                                <zen-button
                                    variant="primary"
                                    size="field"
                                    :processing="loading"
                                    @click="applyFilters"
                                    data-testid="audit-apply-filters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>
                    </div>

                    <!-- Loading -->
                    <zen-loading-indicator
                        v-if="loading && events.length === 0"
                    />

                    <!-- Error -->
                    <zen-message-block v-if="error" type="error">
                        {{ error }}
                    </zen-message-block>

                    <!-- Results -->
                    <div v-if="!loading || events.length > 0" class="space-y-4">
                        <!-- Empty state -->
                        <zen-empty-table-message
                            v-if="events.length === 0 && !loading"
                            compact
                            data-testid="audit-empty"
                            icon="ZenIconHistory"
                        >
                            No audit events found matching your filters.
                        </zen-empty-table-message>

                        <!-- Event list -->
                        <div
                            v-if="events.length > 0"
                            class="bg-white shadow-sm rounded-lg overflow-hidden"
                            data-testid="audit-event-list"
                        >
                            <ul class="divide-y divide-taupe-200">
                                <li
                                    v-for="event in events"
                                    :key="event.ulid"
                                    class="hover:bg-taupe-50 cursor-pointer"
                                    @click="toggleEventDetail(event.ulid)"
                                    :data-testid="'audit-event-' + event.ulid"
                                >
                                    <!-- Summary row -->
                                    <div class="px-4 py-4 sm:px-6">
                                        <div
                                            class="flex items-center justify-between"
                                        >
                                            <div
                                                class="flex items-center space-x-3 min-w-0"
                                            >
                                                <span
                                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                                    :class="
                                                        actionBadgeClass(
                                                            event.action
                                                        )
                                                    "
                                                >
                                                    {{
                                                        formatAction(
                                                            event.action
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    v-if="event.subject_label"
                                                    class="text-sm font-medium text-taupe-900 truncate"
                                                >
                                                    {{ event.subject_label }}
                                                </span>
                                            </div>
                                            <div
                                                class="ml-2 flex items-center space-x-4 shrink-0"
                                            >
                                                <span
                                                    class="text-sm text-taupe-500"
                                                >
                                                    {{
                                                        event.actor?.name ||
                                                        event.actor?.email ||
                                                        'System'
                                                    }}
                                                </span>
                                                <zen-date-time
                                                    :value="event.created_at"
                                                    human
                                                    class="text-sm text-taupe-400"
                                                />
                                                <svg
                                                    class="h-5 w-5 text-taupe-400 transition-transform"
                                                    :class="{
                                                        'rotate-90':
                                                            expandedEvents.has(
                                                                event.ulid
                                                            ),
                                                    }"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                                        clip-rule="evenodd"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Detail panel -->
                                    <div
                                        v-if="expandedEvents.has(event.ulid)"
                                        class="px-4 pb-4 sm:px-6 bg-taupe-50 border-t border-taupe-100"
                                        @click.stop
                                    >
                                        <dl
                                            class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4 text-sm"
                                        >
                                            <div>
                                                <dt
                                                    class="font-medium text-taupe-500"
                                                >
                                                    Actor
                                                </dt>
                                                <dd class="mt-1 text-taupe-900">
                                                    {{
                                                        event.actor?.name ||
                                                        'N/A'
                                                    }}
                                                    <span
                                                        v-if="
                                                            event.actor?.email
                                                        "
                                                        class="text-taupe-500"
                                                    >
                                                        ({{
                                                            event.actor.email
                                                        }})
                                                    </span>
                                                </dd>
                                            </div>

                                            <div>
                                                <dt
                                                    class="font-medium text-taupe-500"
                                                >
                                                    Action
                                                </dt>
                                                <dd class="mt-1 text-taupe-900">
                                                    {{ event.action }}
                                                </dd>
                                            </div>

                                            <div>
                                                <dt
                                                    class="font-medium text-taupe-500"
                                                >
                                                    Subject
                                                </dt>
                                                <dd class="mt-1 text-taupe-900">
                                                    {{
                                                        formatSubjectType(
                                                            event.subject_type
                                                        )
                                                    }}
                                                    <span
                                                        v-if="
                                                            event.subject_label
                                                        "
                                                        class="font-medium"
                                                    >
                                                        —
                                                        {{
                                                            event.subject_label
                                                        }}
                                                    </span>
                                                </dd>
                                            </div>

                                            <div>
                                                <dt
                                                    class="font-medium text-taupe-500"
                                                >
                                                    Time
                                                </dt>
                                                <dd class="mt-1 text-taupe-900">
                                                    <zen-date-time
                                                        :value="
                                                            event.created_at
                                                        "
                                                        tz
                                                    />
                                                </dd>
                                            </div>

                                            <div v-if="event.ip_address">
                                                <dt
                                                    class="font-medium text-taupe-500"
                                                >
                                                    IP Address
                                                </dt>
                                                <dd class="mt-1 text-taupe-900">
                                                    {{ event.ip_address }}
                                                </dd>
                                            </div>

                                            <div v-if="event.request_id">
                                                <dt
                                                    class="font-medium text-taupe-500"
                                                >
                                                    Request ID
                                                </dt>
                                                <dd
                                                    class="mt-1 text-taupe-900 font-mono text-xs"
                                                >
                                                    {{ event.request_id }}
                                                </dd>
                                            </div>

                                            <!-- Before/After diff -->
                                            <div
                                                v-if="
                                                    event.before || event.after
                                                "
                                                class="sm:col-span-2"
                                            >
                                                <dt
                                                    class="font-medium text-taupe-500 mb-2"
                                                >
                                                    Changes
                                                </dt>
                                                <dd class="mt-1">
                                                    <div
                                                        class="grid grid-cols-1 sm:grid-cols-2 gap-4"
                                                    >
                                                        <div
                                                            v-if="event.before"
                                                        >
                                                            <span
                                                                class="text-xs font-medium text-red-600 uppercase tracking-wide"
                                                            >
                                                                Before
                                                            </span>
                                                            <pre
                                                                class="mt-1 bg-red-50 rounded p-3 text-xs text-taupe-700 overflow-x-auto whitespace-pre-wrap break-words"
                                                                >{{
                                                                    formatJson(
                                                                        event.before
                                                                    )
                                                                }}</pre>
                                                        </div>
                                                        <div v-if="event.after">
                                                            <span
                                                                class="text-xs font-medium text-green-600 uppercase tracking-wide"
                                                            >
                                                                After
                                                            </span>
                                                            <pre
                                                                class="mt-1 bg-green-50 rounded p-3 text-xs text-taupe-700 overflow-x-auto whitespace-pre-wrap break-words"
                                                                >{{
                                                                    formatJson(
                                                                        event.after
                                                                    )
                                                                }}</pre>
                                                        </div>
                                                    </div>
                                                </dd>
                                            </div>
                                        </dl>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <!-- Pagination -->
                        <div v-if="hasMore" class="flex justify-center pt-4">
                            <zen-button
                                variant="secondary"
                                size="medium"
                                :processing="loadingMore"
                                @click="loadMore"
                                data-testid="audit-load-more"
                            >
                                Load More
                            </zen-button>
                        </div>
                    </div>
                </template>
            </div>
        </section>
    </main>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import AuditService from '@/services/audit.service'
import UserService from '@/services/user.service'
import FlagService from '@/services/flag.service'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { usePlan } from '@/composables/usePlan'
import { useSessionStore } from '@/stores/session'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

const { can } = usePermissions()
const { hasPlanFeature } = usePlan()
const canViewAudit = computed(
    () => can(PERMISSIONS.AUDIT_LOG_READ) && hasPlanFeature('audit_log_access')
)

const sessionStore = useSessionStore()
const { current } = useCurrentEnvironment()
const project = computed(() =>
    (sessionStore.data?.projects?.data ?? []).find(
        (p) => p.key === current.value.projectKey
    )
)

const loading = ref(false)
const loadingMore = ref(false)
const { run } = useCancelableLoad({ initialLoading: false })
const exporting = ref(false)
const error = ref('')
const events = ref([])
const nextCursor = ref(null)
const hasMore = ref(false)
const expandedEvents = ref(new Set())
const actors = ref([])
const flags = ref([])

const filters = reactive({
    action: '',
    subject_type: '',
    actor_ulid: '',
    flag_ulid: '',
    created_from: '',
    created_to: '',
})

const actionOptions = [
    { value: 'flag.created', label: 'Flag Created' },
    { value: 'flag.updated', label: 'Flag Updated' },
    { value: 'flag.deleted', label: 'Flag Deleted' },
    { value: 'target.published', label: 'Target Published' },
    { value: 'target.rollback', label: 'Target Rollback' },
    { value: 'project.created', label: 'Project Created' },
    { value: 'project.updated', label: 'Project Updated' },
    { value: 'project.deleted', label: 'Project Deleted' },
    { value: 'environment.created', label: 'Environment Created' },
    { value: 'environment.updated', label: 'Environment Updated' },
    { value: 'environment.deleted', label: 'Environment Deleted' },
    { value: 'segment.created', label: 'Segment Created' },
    { value: 'segment.updated', label: 'Segment Updated' },
    { value: 'segment.deleted', label: 'Segment Deleted' },
    { value: 'user.created', label: 'User Created' },
    { value: 'user.updated', label: 'User Updated' },
    { value: 'user.deleted', label: 'User Deleted' },
    { value: 'webhook.created', label: 'Webhook Created' },
    { value: 'webhook.updated', label: 'Webhook Updated' },
    { value: 'webhook.deleted', label: 'Webhook Deleted' },
    { value: 'grant.created', label: 'Grant Created' },
    { value: 'grant.revoked', label: 'Grant Revoked' },
]

const subjectTypeOptions = [
    'flag',
    'target',
    'project',
    'environment',
    'segment',
    'user',
    'webhook',
    'grant',
    'account',
    'context',
]

function formatAction(action) {
    if (!action) return ''
    return action
        .split('.')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
}

function formatSubjectType(type) {
    if (!type) return 'Unknown'
    return type.charAt(0).toUpperCase() + type.slice(1)
}

function actionBadgeClass(action) {
    if (!action) return 'bg-taupe-100 text-taupe-800'
    if (action.endsWith('.created')) return 'bg-green-100 text-green-800'
    if (action.endsWith('.deleted') || action.endsWith('.revoked'))
        return 'bg-red-100 text-red-800'
    if (action.endsWith('.updated')) return 'bg-blue-100 text-blue-800'
    if (action.includes('publish')) return 'bg-purple-100 text-purple-800'
    if (action.includes('rollback')) return 'bg-yellow-100 text-yellow-800'
    return 'bg-taupe-100 text-taupe-800'
}

function formatJson(obj) {
    if (!obj) return ''
    return JSON.stringify(obj, null, 2)
}

function toggleEventDetail(ulid) {
    const expanded = new Set(expandedEvents.value)
    if (expanded.has(ulid)) {
        expanded.delete(ulid)
    } else {
        expanded.add(ulid)
    }
    expandedEvents.value = expanded
}

// The "To" date input is day-only, but the API compares `created_to`
// exactly (`<=`) rather than treating it as a whole day, so a bare date
// would cut off at midnight and silently exclude that day's events.
function widenCreatedTo(createdTo) {
    return createdTo ? `${createdTo}T23:59:59Z` : createdTo
}

async function fetchEvents(append = false) {
    if (!canViewAudit.value) return

    if (append) {
        loadingMore.value = true
    } else {
        loading.value = true
        error.value = ''
    }

    const params = {
        ...filters,
        scope_project_ulid: project.value?.ulid ?? '',
        created_to: widenCreatedTo(filters.created_to),
    }
    if (append && nextCursor.value) {
        params.cursor = nextCursor.value
    }

    await run(
        (signal) => AuditService.list(params, { signal }),
        (response) => {
            const newItems = response.data?.items ?? []
            const meta = response.data?.meta ?? {}

            if (append) {
                events.value = [...events.value, ...newItems]
            } else {
                events.value = newItems
            }

            hasMore.value = meta.pagination?.has_more ?? false
            nextCursor.value = meta.pagination?.next_cursor ?? null
        },
        (err) => {
            error.value =
                err.message || 'Failed to load audit events. Please try again.'
        },
        () => {
            loading.value = false
            loadingMore.value = false
        }
    )
}

function applyFilters() {
    nextCursor.value = null
    expandedEvents.value = new Set()
    fetchEvents(false)
}

function clearFilters() {
    filters.action = ''
    filters.subject_type = ''
    filters.actor_ulid = ''
    filters.flag_ulid = ''
    filters.created_from = ''
    filters.created_to = ''
    applyFilters()
}

function loadMore() {
    fetchEvents(true)
}

function defaultDateRange() {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)

    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    }
}

function exportCsv() {
    if (!filters.created_from || !filters.created_to) {
        const { startDate, endDate } = defaultDateRange()
        filters.created_from = filters.created_from || startDate
        filters.created_to = filters.created_to || endDate
    }

    exporting.value = true

    AuditService.exportCsv({
        ...filters,
        scope_project_ulid: project.value?.ulid ?? '',
        created_to: widenCreatedTo(filters.created_to),
    })
        .then((blob) => {
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `audit-log-${filters.created_from}-to-${filters.created_to}.csv`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        })
        .catch((err) => {
            error.value =
                err.message || 'Failed to export audit log. Please try again.'
        })
        .finally(() => {
            exporting.value = false
        })
}

function refresh() {
    fetchEvents()

    if (canViewAudit.value) {
        UserService.list()
            .then((response) => {
                actors.value = response.data ?? []
            })
            .catch(() => {
                actors.value = []
            })

        if (project.value) {
            FlagService.list(project.value.key)
                .then((response) => {
                    flags.value = response.data ?? []
                })
                .catch(() => {
                    flags.value = []
                })
        }
    }
}

onMounted(refresh)

watch(
    () => `${current.value.projectKey}:${current.value.environmentKey}`,
    refresh
)
</script>
